
import { Ticket, Hire, Driver, Organization } from '../types';
import { normalizeStage } from '../utils/stage';
import { inferEnforcementType, type EnforcementType } from '../utils/enforcement';
import { getTecFormRecommendation } from '../utils/tecForms';
import { calculateTicketDeadlines, canTransferLiability, type DeadlineWindow, type TicketDeadlines } from './deadlineService';
import type { LiabilityDecision, LiabilityResult } from './liabilityEngine';
import type { DriverInvoiceStatus } from './driverInvoiceService';
import type { RecoveryDecision } from './recoveryDecisionService';

export type ActionRecommendation = 'transfer' | 'pay' | 'appeal' | 'investigate';

export interface RecommendationResult {
  action: ActionRecommendation;
  confidence: 'High' | 'Medium' | 'Low';
  reason: string;
  urgent: boolean;

  // NEW: Deadline awareness
  blockedActions?: ActionRecommendation[];
  deadlineWarnings?: string[];
  nextDeadline?: DeadlineWindow;

  // NEW: Liability context
  liabilityParty?: LiabilityResult;
  evidenceTier?: 1 | 2 | 3 | 4 | 5;
  legalConfidence?: number;

  // NEW: Driver invoice status
  driverInvoiceStatus?: 'pending' | 'invoiced' | 'sent' | 'paid';
  driverInvoiceAmount?: number;
  recoveryAction?: RecoveryDecision['action'];
  recoveryDecision?: RecoveryDecision;
}

const formatUkDate = (date: Date): string => date.toLocaleDateString('en-GB');

/**
 * FleetMind Recommendation Engine (Context-Aware Enhanced)
 *
 * Provides intelligent, context-aware recommendations based on document type, intent, and stage.
 *
 * Key Features:
 * 0. DOCUMENT CONTEXT AWARENESS (NEW):
 *    - Distinguishes between warning notices, PCNs, NtOs, CCs, and OFRs
 *    - Reads document intent and key phrases (e.g., "NO PAYMENT REQUIRED")
 *    - Prevents inappropriate recommendations (e.g., "pay" on warning notices)
 *    - Provides proactive guidance for pre-PCN documents
 * 1. Stage-aware logic (Fleet intake at NtO/postal PCN → Charge Cert → OfR)
 * 2. Enforcement type detection (statutory vs private):
 *    - Statutory (Council/TfL) under TMA 2004 → uses TEC forms (TE7/TE9 or PE2/PE3 depending on charge type)
 *    - Private parking under PoFA 2012 → operator appeal then POPLA/IAS
 * 3. Hire firm accountability: Flags process failures when tickets escalate with linked hires
 * 4. Remedies at all stages: Even at CC/OFR, recommends valid challenge paths (non-receipt, procedural errors)
 * 5. Root cause analysis: Prompts investigation when escalation indicates systemic failure
 *
 * Priority Order (OPTIMIZED FOR HIRE FIRMS):
 * 1. Document context check (is this even a penalty?)
 * 2. Transfer liability check (PRIORITY - hire firms should transfer, not pay!)
 * 3. Stage-specific logic (what stage are we at?)
 * 4. Procedural grounds (are there technical errors?)
 * 5. Missing hire prompt (assign hire to enable transfer)
 * 6. Default investigation (gather more data)
 *
 * The engine balances legal options with business accountability, ensuring hire firms
 * understand both their remedies AND their process failures when tickets escalate unnecessarily.
 */

/**
 * Helper: Determine if ticket is statutory civil enforcement or private parking
 * Statutory = Council/TfL parking contraventions (TMA 2004) - uses TEC forms
 * Private = Contractual parking charge (PoFA 2012) - uses operator appeal then POPLA/IAS
 */
const getEnforcementType = (ticket: Ticket): EnforcementType =>
  inferEnforcementType({
    issuing_authority: ticket.issuing_authority,
    document_type: ticket.document_type,
    document_intent: ticket.document_intent,
    key_phrases: ticket.key_phrases,
    stage: ticket.stage
  });

const getPrivateAppealChannel = (ticket: Ticket): string => {
  const text = [
    ticket.issuing_authority,
    ticket.document_intent,
    ...(ticket.key_phrases || [])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (text.includes('popla') || text.includes('bpa')) return 'POPLA';
  if (text.includes('ias') || text.includes('ipc') || text.includes('independent appeals service')) return 'IAS';
  return 'POPLA/IAS (depending on BPA/IPC membership)';
};

export const RecommendationService = {
  analyze(
    ticket: Ticket,
    linkedHire: Hire | null,
    linkedDriver: Driver | null,
    organization: Organization | null = null,
    liabilityDecision?: LiabilityDecision | null,
    deadlines?: TicketDeadlines | null,
    invoiceStatus?: DriverInvoiceStatus | null,
    recoveryDecision?: RecoveryDecision | null
  ): RecommendationResult {
    const now = new Date();
    const issueDate = new Date(ticket.issue_date);
    const contraventionDate = new Date(ticket.contravention_date || ticket.issue_date);

    const daysSinceIssue = Math.floor((now.getTime() - issueDate.getTime()) / (1000 * 3600 * 24));
    const daysToIssue = Math.floor((issueDate.getTime() - contraventionDate.getTime()) / (1000 * 3600 * 24));

    // Normalize stage for comparison
    const enforcementType = getEnforcementType(ticket);
    const isPrivate = enforcementType === 'private';
    const stage = normalizeStage(ticket.stage || (isPrivate ? 'Parking Charge Notice' : 'PCN Issued'), enforcementType).toLowerCase();
    const tecForms = enforcementType === 'statutory' ? getTecFormRecommendation(ticket, now) : null;
    const tecFormBundle = tecForms
      ? `${tecForms.primaryForm}${tecForms.isOutOfTime ? ` + ${tecForms.outOfTimeForm}` : ''}`
      : 'a statutory declaration';
    const tecFormOutOfTimeNote = tecForms && !tecForms.isOutOfTime
      ? ` If you are out of time, also file ${tecForms.outOfTimeForm}.`
      : '';

    // ==========================================
    // DEADLINE CALCULATION (GLOBAL)
    // ==========================================
    // Calculate deadlines once at the top so ALL code paths can use them
    const calculatedDeadlines = deadlines ?? calculateTicketDeadlines(ticket);
    const blockedActions: ActionRecommendation[] = [];
    const deadlineWarnings: string[] = [];

    // Check transfer window eligibility
    const transferCheck = canTransferLiability(ticket);
    if (!transferCheck.allowed && linkedHire) {
      blockedActions.push('transfer');
      deadlineWarnings.push(`⚠ ${transferCheck.reason}`);
    }

    // Check appeal window
    const appealWindow = calculatedDeadlines.appealWindow;
    if (appealWindow && !appealWindow.isOpen) {
      blockedActions.push('appeal');
      deadlineWarnings.push(`⚠ Appeal window expired ${Math.abs(appealWindow.daysRemaining)} days ago`);
    }

    // Integrate liability engine decision (if provided)
    const liabilityAction = liabilityDecision?.recommended_action;
    const evidenceTier = liabilityDecision?.evidence_tier || 5;
    const legalConfidence = liabilityDecision?.legal_confidence || 0;

    // ==========================================
    // PRIORITY 0: DOCUMENT TYPE & CONTEXT CHECK
    // ==========================================
    // Check if this is a warning notice or other pre-PCN document
    // This takes absolute priority over all other logic

    if (ticket.document_type === 'WARNING_NOTICE' || ticket.payment_required === false) {
      // This is a warning notice - no payment required, but action may be needed
      const hasHire = linkedHire && (linkedHire.agreement_url || linkedHire.agreement_signed_at);

      if (hasHire) {
        return {
          action: 'investigate',
          confidence: 'High',
          reason: `WARNING NOTICE (No Payment Required): Vehicle ${ticket.vrn} was detected in ${ticket.location} on ${formatUkDate(new Date(ticket.contravention_date))} without proper authorization. This is NOT a penalty - no payment is due. However, vehicle was on hire to ${linkedHire.driver_name} (Ref: ${linkedHire.hire_ref}). ACTION REQUIRED: (1) Notify hirer about the warning and zone requirements, (2) Ensure hirer registers/pays for future access, (3) Update internal processes to prevent future violations. Future contraventions will result in £${ticket.amount} PCN (£${ticket.discount_amount || (ticket.amount / 2)} if paid early). This is a process improvement opportunity.`,
          urgent: false,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined
        };
      }

      return {
        action: 'investigate',
        confidence: 'High',
        reason: `WARNING NOTICE (No Payment Required): Vehicle ${ticket.vrn} was detected in ${ticket.location} without proper authorization. ${ticket.document_intent || 'This is a courtesy warning - no payment is due at this time.'}. Future contraventions will result in £${ticket.amount} PCN (£${ticket.discount_amount || (ticket.amount / 2)} if paid early). ACTION REQUIRED: (1) If you need access to this area, register the vehicle or purchase required permits, (2) Otherwise, avoid this location. No hire record found - verify if this was staff use or unauthorized use.`,
        urgent: false,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined
      };
    }

    // ==========================================
    // PRIORITY 0.5: BUSINESS MODEL CHECK
    // ==========================================
    // Route recommendations based on organization's business model
    const businessModel = organization?.business_model || 'hire_firm_registered';
    const isFleetOperator = businessModel === 'fleet_operator';
    const isStartupHireFirm = businessModel === 'hire_firm_startup';

    // FLEET OPERATOR PATH: Skip hire checks entirely
    if (isFleetOperator && !isPrivate) {
      // Check procedural grounds first
      if (daysToIssue > 28 && (stage.includes('pcn') || stage.includes('notice to owner'))) {
        const daysLeft = 28 - daysSinceIssue;
        return {
          action: 'appeal',
          confidence: 'High',
          reason: `Procedural Error: PCN issued ${daysToIssue} days after contravention (legal limit is 28 days). As a fleet operator, you can appeal on procedural grounds. ${daysLeft > 0 ? `${daysLeft} days left to appeal.` : 'URGENT: Deadline may have passed!'}`,
          urgent: daysLeft < 7,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      // Check discount period
      if (ticket.discount_amount && daysSinceIssue <= 14) {
        return {
          action: 'pay',
          confidence: 'High',
          reason: `Fleet Operator - Early Payment Option: Pay £${ticket.discount_amount} within ${14 - daysSinceIssue} days to receive 50% discount (full amount: £${ticket.amount}). As a fleet operator, you are directly liable for this ticket.`,
          urgent: daysSinceIssue > 10,
          nextDeadline: calculatedDeadlines.discountPeriod || calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      // Check if at late stage
      if (stage.includes('charge certificate') || stage.includes('order for recovery')) {
        if (stage.includes('order for recovery')) {
          const formSystem = tecForms ? 'Traffic Enforcement Centre (TEC)' : 'the issuer\'s statutory declaration process';
          return {
            action: 'investigate',
            confidence: 'High',
            reason:
              `Fleet Operator - Order for Recovery: Court enforcement is active. Before paying £${ticket.amount}, first assess whether you genuinely received the earlier notices (Notice to Owner and Charge Certificate). ` +
              `If you did NOT receive them, you may be eligible to file ${tecFormBundle} with ${formSystem} to reset the case.${tecFormOutOfTimeNote} ` +
              `WARNING: False declarations can be a criminal offence/perjury. If you did receive the notices (or have no valid grounds), pay immediately to stop bailiff fees and court costs.`,
            urgent: true,
            nextDeadline: calculatedDeadlines.nextDeadline || undefined,
            blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
            deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
          };
        }
        return {
          action: 'pay',
          confidence: 'High',
          reason: `Fleet Operator - Late Stage: This ticket has escalated to ${stage.includes('charge certificate') ? 'Charge Certificate' : 'Order for Recovery'} stage. Amount increased by 50%. Pay immediately to stop further enforcement action (bailiffs, court costs). Amount due: £${ticket.amount}.`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      // Default: Investigate or pay
      return {
        action: 'investigate',
        confidence: 'Medium',
        reason: `Fleet Operator Mode: Review this £${ticket.amount} ticket to determine action. Options: (1) Pay within ${28 - daysSinceIssue} days${ticket.discount_amount ? ` (£${ticket.discount_amount} if paid within 14 days)` : ''}, (2) Appeal if valid grounds exist (procedural errors, incorrect details), (3) Gather evidence before deciding.`,
        urgent: daysSinceIssue > 14,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    if (isFleetOperator && isPrivate) {
      const appealChannel = getPrivateAppealChannel(ticket);
      const appealWindow = calculatedDeadlines.appealWindow;
      const appealNote = appealWindow?.isOpen
        ? `You likely have ${appealWindow.daysRemaining} days left to appeal.`
        : 'Initial appeal window may have closed; consider complaint or settlement.';

      if (stage.includes('letter before claim') || stage.includes('court claim')) {
        return {
          action: 'investigate',
          confidence: 'High',
          reason: `Private Parking - Legal Stage: This has reached ${stage.includes('letter before claim') ? 'Letter Before Claim' : 'Court Claim'} stage. Treat as legal correspondence. Review evidence, confirm driver/keeper liability, and consider formal response under the pre-action protocol. Do NOT ignore.`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      if (stage.includes('debt recovery')) {
        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `Private Parking - Debt Recovery: The operator has passed this to debt recovery. Appeals are usually closed at this stage, but you can still dispute with the operator or consider settlement. ${appealNote}`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      return {
        action: 'appeal',
        confidence: 'Medium',
        reason: `Private Parking - Early Stage: Appeal to the operator first, then ${appealChannel} if rejected. Gather evidence (signage, permits, hire agreement, timestamps). ${appealNote}`,
        urgent: appealWindow ? appealWindow.daysRemaining < 7 : false,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    // ==========================================
    // PRIORITY 1: DRIVER INVOICE STATUS CHECK (NEW!)
    // ==========================================
    if (invoiceStatus?.status === 'paid') {
      return {
        action: 'investigate',
        confidence: 'High',
        reason: `Driver already paid invoice £${invoiceStatus.amount} on ${formatUkDate(new Date(invoiceStatus.paidAt!))}. Fleet has recovered costs. Mark ticket as resolved or pay authority if required.`,
        urgent: false,
        blockedActions,
        driverInvoiceStatus: 'paid',
        driverInvoiceAmount: invoiceStatus.amount ?? undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    if (recoveryDecision?.action === 'mark_recovered') {
      return {
        action: 'investigate',
        confidence: 'High',
        reason: `DRIVER RECOVERY COMPLETE: ${recoveryDecision.hint} Recovered amount: £${recoveryDecision.recoverableAmount.toFixed(2)}.`,
        urgent: false,
        blockedActions,
        driverInvoiceStatus: 'paid',
        driverInvoiceAmount: recoveryDecision.recoverableAmount,
        recoveryAction: recoveryDecision.action,
        recoveryDecision,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    if (recoveryDecision?.action === 'review_dispute') {
      return {
        action: 'investigate',
        confidence: 'High',
        reason: `DRIVER INVOICE DISPUTED: ${recoveryDecision.hint} Review hire agreement, PCN evidence, and invoice line items before continuing recovery.`,
        urgent: true,
        blockedActions,
        driverInvoiceStatus: 'invoiced',
        driverInvoiceAmount: recoveryDecision.recoverableAmount,
        recoveryAction: recoveryDecision.action,
        recoveryDecision,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    if (invoiceStatus?.status === 'draft' || invoiceStatus?.status === 'sent') {
      deadlineWarnings.push(`Driver invoiced £${invoiceStatus.amount} (due ${formatUkDate(new Date(invoiceStatus.dueDate!))}) - awaiting payment`);
    }

    if (recoveryDecision?.action === 'await_driver_payment') {
      return {
        action: 'investigate',
        confidence: 'High',
        reason: `DRIVER PAYMENT PENDING: ${recoveryDecision.hint} Recoverable amount: £${recoveryDecision.recoverableAmount.toFixed(2)}.`,
        urgent: false,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence,
        recoveryAction: recoveryDecision.action,
        recoveryDecision
      };
    }

    // ==========================================
    // PRIORITY 2: TRANSFER LIABILITY CHECK (REFACTORED)
    // ==========================================
    // CRITICAL FOR HIRE FIRMS: Check for transfer BEFORE all other logic
    // If vehicle was on hire, the hire firm should TRANSFER liability, not pay or appeal
    // This check happens at Priority 2 to maximize value for hire firms

    // Only recommend transfer if:
    // 1. Hire exists AND
    // 2. Transfer window is OPEN (not expired) AND
    // 3. Liability engine supports transfer (if available)

    if (linkedHire && transferCheck.allowed && !blockedActions.includes('transfer') && !isFleetOperator) {
      const legalWarning = isStartupHireFirm && !organization?.legal_entity_verified
        ? ' ⚠️ WARNING: Your business is not legally verified. Transferred liability may be challenged by enforcement authorities. We recommend completing Companies House registration to strengthen your legal position.'
        : '';

      const transferWindow = calculatedDeadlines.transferWindow;
      const daysLeft = transferWindow?.daysRemaining || 0;
      const windowCloses = transferWindow?.deadlineDate ? formatUkDate(new Date(transferWindow.deadlineDate)) : '';

      // Use liability decision if available
      if (liabilityAction === 'transfer' && evidenceTier <= 2) {
        return {
          action: 'transfer',
          confidence: evidenceTier === 1 ? 'High' : 'Medium',
          reason: `TRANSFER LIABILITY RECOMMENDED: ${liabilityDecision?.legal_reason_text}. Window closes in ${daysLeft} days (${windowCloses}).${legalWarning}`,
          urgent: transferWindow?.urgencyLevel === 'urgent',
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      // Fallback to existing logic (if no liability decision)
      if (linkedHire.agreement_url || linkedHire.agreement_signed_at) {
        return {
          action: 'transfer',
          confidence: isStartupHireFirm ? 'Medium' : 'High',
          reason: `TRANSFER LIABILITY RECOMMENDED: Vehicle ${ticket.vrn} was on hire to ${linkedHire.driver_name} (Ref: ${linkedHire.hire_ref}) at time of contravention. Hire firm is NOT liable - transfer to hirer within 28-day statutory window. Evidence pack ready: signed hire agreement + liability clause + statement of liability. This protects hire firm from paying £${ticket.amount}. Window closes in ${daysLeft} days (${windowCloses}).${legalWarning}`,
          urgent: transferWindow?.urgencyLevel === 'urgent' || daysSinceIssue > 14 || stage.includes('notice to owner'),
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      } else {
        return {
          action: 'transfer',
          confidence: 'Medium',
          reason: `TRANSFER LIABILITY RECOMMENDED: Vehicle ${ticket.vrn} was on hire to ${linkedHire.driver_name} at time of contravention, but signed hire agreement is MISSING. URGENT: Locate signed agreement + liability clause to complete transfer within 28-day window. Without transfer, hire firm becomes liable for £${ticket.amount}. Time remaining: ${daysLeft} days (${windowCloses}).${legalWarning}`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }
    }

    // If transfer window expired but hire exists, explain why transfer blocked
    if (linkedHire && !transferCheck.allowed && !isFleetOperator) {
      return {
        action: 'investigate',
        confidence: 'High',
        reason: `TRANSFER WINDOW EXPIRED: ${transferCheck.reason}. Hire found for ${linkedHire.driver_name}, but cannot transfer at ${ticket.stage} stage. ${appealWindow?.isOpen ? 'Appeal to TPT/POPLA still available.' : 'Consider statutory declaration or payment.'}`,
        urgent: true,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence
      };
    }

    // Check if hire is missing for an active PCN/NtO (Priority 2B)
    // For hire firms, missing hire record = urgent data collection task, NOT automatic payment
    // Skip for fleet operators who don't have hire records
    if (!linkedHire && transferCheck.allowed && !isFleetOperator && ticket.status !== 'Paid' && ticket.status !== 'appeal_pending') {
      const transferWindow = calculatedDeadlines.transferWindow;
      const daysLeft = transferWindow?.daysRemaining || 0;

      return {
        action: 'investigate',
        confidence: 'High',
        reason: `NO HIRE RECORD FOUND: PCN for ${ticket.vrn} on ${formatUkDate(new Date(ticket.contravention_date))} at ${ticket.location}. URGENT ACTION REQUIRED: (1) Check hire records - was this vehicle on hire? (2) If yes, assign hire record NOW to enable transfer of liability (${daysLeft} days left). (3) If no, verify if this was staff use, unauthorized use, or error. DO NOT PAY without checking hire status first - hire firms should transfer, not pay. Amount at risk: £${ticket.amount}.`,
        urgent: transferWindow?.urgencyLevel === 'urgent' || daysSinceIssue > 14,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence
      };
    }

    if (recoveryDecision?.action === 'invoice_driver' || recoveryDecision?.action === 'pay_then_recover') {
      const warnings = recoveryDecision.warnings.length > 0 ? ` ${recoveryDecision.warnings.join(' ')}` : '';
      return {
        action: 'investigate',
        confidence: recoveryDecision.action === 'invoice_driver' ? 'High' : 'Medium',
        reason: `DRIVER RECOVERY: ${recoveryDecision.hint} Recoverable amount: £${recoveryDecision.recoverableAmount.toFixed(2)}${recoveryDecision.adminFeeAmount > 0 ? ` including £${recoveryDecision.adminFeeAmount.toFixed(2)} admin fee` : ''}.${warnings}`,
        urgent: recoveryDecision.action === 'pay_then_recover',
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence,
        recoveryAction: recoveryDecision.action,
        recoveryDecision
      };
    }

    if (isPrivate) {
      const appealChannel = getPrivateAppealChannel(ticket);
      const appealWindow = calculatedDeadlines.appealWindow;
      const appealNote = appealWindow?.isOpen
        ? `You likely have ${appealWindow.daysRemaining} days left to appeal.`
        : 'Initial appeal window may have closed; consider complaint or settlement.';

      if (stage.includes('letter before claim') || stage.includes('court claim')) {
        return {
          action: 'investigate',
          confidence: 'High',
          reason: `PRIVATE PARKING - LEGAL STAGE: This has reached ${stage.includes('letter before claim') ? 'Letter Before Claim' : 'Court Claim'} stage. Treat as legal correspondence. Review evidence, confirm driver/keeper liability, and consider a formal response. Do NOT ignore.`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence
        };
      }

      if (stage.includes('debt recovery')) {
        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `PRIVATE PARKING - DEBT RECOVERY: The operator has escalated to debt recovery. Appeals are usually closed at this stage, but you can still dispute with the operator or consider settlement. ${appealNote}`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence
        };
      }

      if (stage.includes('final reminder') || stage.includes('reminder')) {
        return {
          action: appealWindow?.isOpen ? 'appeal' : 'investigate',
          confidence: 'Medium',
          reason: `PRIVATE PARKING - REMINDER STAGE: Appeal to the operator first, then ${appealChannel} if rejected. Gather evidence (signage, permits, hire agreement, timestamps). ${appealNote}`,
          urgent: appealWindow ? appealWindow.daysRemaining < 7 : false,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence
        };
      }

      return {
        action: appealWindow?.isOpen ? 'appeal' : 'investigate',
        confidence: 'Medium',
        reason: `PRIVATE PARKING - EARLY STAGE: Appeal to the operator first, then ${appealChannel} if rejected. Gather evidence (signage, permits, hire agreement, timestamps). ${appealNote}`,
        urgent: appealWindow ? appealWindow.daysRemaining < 7 : false,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
        liabilityParty: liabilityDecision?.legal_result,
        evidenceTier,
        legalConfidence
      };
    }

    // ==========================================
    // PRIORITY 2: STAGE-AWARE ESCALATION LOGIC
    // ==========================================
    // Check if we're past the appeal window (CC/OFR stages)
    // Fleet Mode: hire firms typically begin at NtO / postal PCN.
    // Practical deadlines:
    // - NtO / postal PCN: 28 days to pay/make representations (discount often 14 days if offered)
    // - Charge Certificate: Appeal window closed, only Tribunal appeal or pay
    // - Order for Recovery: Court enforcement, must pay or apply to set aside

    if (!isPrivate && (stage.includes('order for recovery') || stage.includes('te3'))) {
      // CRITICAL: Order for Recovery means the ticket escalated through all prior stages (NtO/postal PCN → CC)
      // This indicates systemic failure if there was a hire - WHY was action not taken earlier?

      const isTEC = Boolean(tecForms);
      const formSystem = isTEC ? 'Traffic Enforcement Centre (TEC)' : 'the issuer’s statutory declaration process';

      // PRIORITY: Check if user qualifies for STATUTORY DECLARATION (non-receipt of earlier notices)
      // This is a legal remedy available at OFR stage to set aside the order
      // Grounds: Never received NtO or Charge Certificate

      // Strong indicators for statutory declaration eligibility:
      const hasNoRecordOfPriorNotices = !ticket.notes?.toLowerCase().includes('received') &&
                                         !ticket.notes?.toLowerCase().includes('nto') &&
                                         !ticket.notes?.toLowerCase().includes('charge certificate');

      const recentlyDiscovered = daysSinceIssue < 21; // Recently became aware of OFR

      const possibleAddressIssue = linkedDriver ?
        (!linkedDriver.address_line1 || !linkedDriver.postcode) : false;

      // SCENARIO 1: Strong case for statutory declaration (non-receipt)
      if (hasNoRecordOfPriorNotices || recentlyDiscovered || possibleAddressIssue) {
        const reasons = [];
        if (hasNoRecordOfPriorNotices) reasons.push('no record of receiving prior notices');
        if (recentlyDiscovered) reasons.push('recently discovered (within 21 days)');
        if (possibleAddressIssue) reasons.push('incomplete address on file may explain non-receipt');

        return {
          action: 'investigate',
          confidence: 'High',
          reason: `ORDER FOR RECOVERY - STATUTORY DECLARATION OPTION AVAILABLE: You may qualify to set aside this order by filing a statutory declaration claiming non-receipt of Notice to Owner and/or Charge Certificate. Indicators: ${reasons.join(', ')}. NEXT STEPS: (1) Confirm you genuinely never received NtO or CC, (2) File ${tecFormBundle} with ${formSystem} within 21 days stating non-receipt (requires sworn statement).${tecFormOutOfTimeNote} (3) If successful, case returns to NtO/CC stage for fresh representations. WARNING: False declaration is perjury - only proceed if genuinely did not receive notices. If rejected or not eligible, must pay immediately (£${ticket.amount} + costs).`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
        };
      }

      // SCENARIO 2: Hire firm accountability - ticket escalated WITH a linked hire
      if (linkedHire && (linkedHire.agreement_url || linkedHire.agreement_signed_at)) {
        // The hire firm failed to transfer at NtO/postal PCN stage - need to understand WHY
        const escalationGap = `Ticket reached Order for Recovery with hire agreement in place. This is a PROCESS FAILURE - liability should have been transferred at NtO/postal PCN stage to ${linkedHire.driver_name}.`;

        // Check if hirer may not have received notices (valid ground for statutory declaration)
        const possibleNonReceipt = !linkedDriver || !linkedDriver.address_line1;

        if (possibleNonReceipt) {
          return {
            action: 'investigate',
            confidence: 'High',
            reason: `ESCALATION FAILURE: ${escalationGap} STATUTORY DECLARATION AVAILABLE: Hirer address incomplete/incorrect (${linkedDriver?.address_line1 || 'missing'}) - strong grounds to argue hirer never received NtO/CC. File ${tecFormBundle} claiming non-receipt within 21 days.${tecFormOutOfTimeNote} If successful, you can transfer liability at earlier stage. If rejected, hire firm liable for full £${ticket.amount} + court costs + bailiff fees. URGENT: Also investigate why transfer was not completed at NtO stage (process failure).`,
            urgent: true
          };
        }

        return {
          action: 'investigate',
          confidence: 'High',
          reason: `ESCALATION FAILURE: ${escalationGap} LIMITED OPTIONS: (1) File statutory declaration claiming you/hirer never received NtO/CC (${tecFormBundle}) - requires sworn statement and genuine non-receipt.${tecFormOutOfTimeNote} (2) If no valid grounds for statutory declaration, PAY IMMEDIATELY (£${ticket.amount}) to stop bailiff enforcement. Root Cause: Why was Hire Ref ${linkedHire.hire_ref} not transferred earlier? This represents internal process failure requiring review.`,
          urgent: true
        };
      }

      // SCENARIO 3: Procedural grounds (late service) - independent of hire status
      if (daysToIssue > 28) {
        const advice = tecForms
          ? `Apply to TEC using ${tecFormBundle} citing procedural impropriety (PCN served outside statutory 28-day limit). Include evidence of dates.${tecFormOutOfTimeNote}`
          : 'Apply to the issuer’s statutory declaration process citing procedural error. Include timeline evidence.';

        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `ORDER FOR RECOVERY - PROCEDURAL ERROR GROUNDS: PCN served ${daysToIssue} days after contravention (statutory limit: 28 days). TWO OPTIONS: (1) ${advice}, (2) File statutory declaration if you never received NtO/CC (separate ground). Choose strongest ground. WARNING: If both fail, pay £${ticket.amount} immediately to avoid bailiff enforcement, warrant of execution, and additional costs (£75-£235). Time-critical - act within 7-14 days.`,
          urgent: true
        };
      }

      // SCENARIO 4: No obvious grounds - assess statutory declaration before payment
      const mayQualifyForStatutoryDeclaration = !ticket.status?.includes('appeal') && daysSinceIssue < 30;

      if (mayQualifyForStatutoryDeclaration) {
        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `ORDER FOR RECOVERY - ASSESS ELIGIBILITY FOR STATUTORY DECLARATION: Before paying £${ticket.amount}, determine if you qualify for statutory declaration to set aside this order. QUESTIONS TO ASSESS: (1) Did you genuinely never receive Notice to Owner (NtO)? (2) Did you genuinely never receive Charge Certificate? (3) Have you moved address/changed mail forwarding? (4) Is this the first notice you've seen? IF YES to any: File ${tecFormBundle} claiming non-receipt within 21 days.${tecFormOutOfTimeNote} You'll need sworn statement - perjury if false. IF NO valid grounds: PAY IMMEDIATELY to stop bailiffs. Court enforcement is active - further delay adds £75-£235 costs.`,
          urgent: true
        };
      }

      // SCENARIO 5: No grounds found - payment mandatory
      return {
        action: 'investigate',
        confidence: 'Medium',
        reason: `ORDER FOR RECOVERY - CONFIRM NON-RECEIPT BEFORE PAYMENT: Court enforcement is active. Before paying £${ticket.amount}, confirm whether you genuinely received the Notice to Owner and/or Charge Certificate. IF you did NOT receive them: file ${tecFormBundle} within the allowed timeframe (truthful statements only—false declarations can be perjury).${tecFormOutOfTimeNote} IF you did receive them or have no valid grounds: pay immediately to stop enforcement and added costs (£75–£235+).`,
        urgent: true
      };
    }

    if (!isPrivate && stage.includes('charge certificate')) {
      // Charge Certificate means NtO/postal PCN stage was missed - amount is now 50% higher
      // For hire firms: This represents a failure to act at earlier stages

      const isTE = enforcementType === 'statutory';
      const appealBody = isTE ? 'Traffic Penalty Tribunal (TPT)' : 'POPLA (Parking on Private Land Appeals)';

      // SCENARIO 1: Hire firm accountability - ticket escalated to CC with hire
      if (linkedHire && (linkedHire.agreement_url || linkedHire.agreement_signed_at)) {
        const lateTransferWarning = `Charge Certificate issued. WARNING: Liability should have been transferred to ${linkedHire.driver_name} at NtO/postal PCN stage. Amount now 50% higher.`;

        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `LATE TRANSFER: ${lateTransferWarning} REMAINING OPTIONS: Appeal to ${appealBody} within 28 days arguing (1) Hirer non-receipt of NtO, or (2) Procedural error. Submit hire agreement + witness statement from hirer confirming non-receipt. If appeal fails, hire firm pays 50% penalty. Alternative: Pay now to prevent Order for Recovery. Investigate: Why was transfer not completed at NtO/postal PCN stage?`,
          urgent: true,
          nextDeadline: calculatedDeadlines.nextDeadline || undefined,
          blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
          deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
          liabilityParty: liabilityDecision?.legal_result,
          evidenceTier,
          legalConfidence
        };
      }

      // SCENARIO 2: Procedural grounds (late service)
      if (daysToIssue > 28) {
        return {
          action: 'appeal',
          confidence: 'Medium',
          reason: `Charge Certificate - Procedural Error: PCN served ${daysToIssue} days late (exceeds 28-day limit). Appeal to ${appealBody} on procedural grounds within 28 days of Charge Cert. Include timeline evidence. WARNING: Amount is 50% higher. If appeal rejected, you pay increased amount. Consider settling if case is weak.`,
          urgent: true
        };
      }

      // SCENARIO 3: Recent CC issuance - check for non-receipt
      if (daysSinceIssue < 7) {
        return {
          action: 'investigate',
          confidence: 'Medium',
          reason: `Charge Certificate just issued (${daysSinceIssue} days ago). If you never received Notice to Owner, you can appeal to ${appealBody} claiming non-receipt within 28 days. You'll need statutory declaration. Otherwise, pay now (amount increased 50%) to prevent Order for Recovery and further costs.`,
          urgent: true
        };
      }

      // SCENARIO 4: No grounds found
      return {
        action: 'pay',
        confidence: 'High',
        reason: `Charge Certificate Stage: Amount increased by 50% (failed to respond to NtO). No hire record or procedural grounds found. Pay immediately to prevent escalation to Order for Recovery (court enforcement + bailiffs). Appeal window for ${appealBody} is 28 days - act now if you have grounds.`,
        urgent: true,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    // ==========================================
    // PRIORITY 3: PROCEDURAL GROUNDS CHECK
    // ==========================================
    // Check for procedural errors (late service) that provide appeal grounds
    // Note: This only triggers if transfer check didn't apply (no hire at PCN/NtO stage)
    // Under General Regulations 2007, a postal PCN must generally be served within 28 days.
    if (!isPrivate && daysToIssue > 28 && (stage.includes('pcn') || stage.includes('notice to owner'))) {
      const daysLeft = 28 - daysSinceIssue;

      return {
        action: 'appeal',
        confidence: 'High',
        reason: `Procedural Error: PCN issued ${daysToIssue} days after contravention (Limit is 28 days). Ground: Procedural Impropriety. ${daysLeft > 0 ? `${daysLeft} days left to appeal.` : 'URGENT: Deadline may have passed!'}`,
        urgent: daysLeft < 7
      };
    }

    // ==========================================
    // PRIORITY 4: EDGE CASES & FALLBACK LOGIC
    // ==========================================
    // These scenarios only trigger if none of the above priority checks matched

    // Edge case: Low-risk driver with no hire record (possible staff use or error)
    if (linkedDriver && linkedDriver.risk_score === 'Low' && !linkedHire && transferCheck.allowed) {
      const daysLeft = calculatedDeadlines.transferWindow?.daysRemaining || 0;
      return {
        action: 'investigate',
        confidence: 'Medium',
        reason: `No hire found for ${ticket.vrn}, but vehicle may be assigned to low-risk driver ${linkedDriver.name}. VERIFY: (1) Was this staff use? (2) Should this be linked to a hire? (3) Is this a technical error? Time left to act: ${daysLeft} days. If hire exists, assign it NOW to enable transfer.`,
        urgent: calculatedDeadlines.transferWindow?.urgencyLevel === 'urgent' || daysSinceIssue > 14,
        nextDeadline: calculatedDeadlines.nextDeadline || undefined,
        blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
        deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined
      };
    }

    // Default fallback (should rarely trigger with new priority logic)
    return {
      action: 'investigate',
      confidence: 'Low',
      reason: `Insufficient data to recommend action for ${ticket.vrn}. NEXT STEPS: (1) If vehicle was on hire at time of contravention, assign hire record to enable transfer of liability. (2) Review document and evidence manually. (3) Verify ticket stage and enforcement type. For hire firms: Always check hire status FIRST before considering payment.`,
      urgent: false,
      nextDeadline: calculatedDeadlines.nextDeadline || undefined,
      blockedActions: blockedActions.length > 0 ? blockedActions : undefined,
      deadlineWarnings: deadlineWarnings.length > 0 ? deadlineWarnings : undefined,
      liabilityParty: liabilityDecision?.legal_result,
      evidenceTier,
      legalConfidence
    };
  }
};
